import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
} from "../../../../redux/features/policyDetailsSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  formatDateAsNumbers,
  generatePolicyCardInfo,
  mapTravelPlanName,
} from "../../../../utils/policyCardUtils";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const TravelCancellationReviewPage = ({ handleNavigationToPolicyOverview }) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { handleNextSubstep, handleFlowChange } = usePolicyCancellationFlow();

  const { t } = useTranslation();

  // Get policy detail. Call API if empty or check redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== TRAVEL POLICY CANCEL DATA DEBUG ===");
  console.log("Full policyDetailsInfo no json:", policyDetailsInfo);
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2),
  );
  console.log(
    "Full policyDetailsPartyInfo:",
    JSON.stringify(policyDetailsPartyInfo, null, 2),
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("PlanName:", policyDetailsInfo?.PlanName);
  console.log("Passengers:", policyDetailsInfo?.Passengers);
  console.log("Covers:", policyDetailsInfo?.Covers);
  console.log("Premium:", policyDetailsInfo?.Premium);
  console.log("=== END DEBUG ===");

  // Helper function to format date to English format (10 July 2025)
  const formatDateToEnglish = (dateStr) => {
    if (!dateStr) return "--";

    try {
      const parts = dateStr.split("/");
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Generate policy card info using utility function
  const policyCardInfo =
    generatePolicyCardInfo(policyDetailsInfo, getClientSource()) || {};

  // Build travel data like TravelDetailsTable format
  const buildTravelTableData = () => {
    const tableData = {};

    // Only add fields that have data, like TravelDetailsTable

    // Passenger 1 is always the policy holder (Party); family members follow.
    const partyName = `${policyDetailsPartyInfo?.FirstName || ""} ${
      policyDetailsPartyInfo?.LastName || ""
    }`.trim();
    tableData[`${translate("Passenger", "مسافر")} 1`] = partyName || "N/A";
    // Additional members — skip empty/placeholder entries (no name).
    (policyDetailsInfo?.Passengers || [])
      .map((passenger) => passenger.FullName || passenger.FirstName)
      .filter(Boolean)
      .forEach((name, index) => {
        tableData[`${translate("Passenger", "مسافر")} ${index + 2}`] = name;
      });

    // Departure Date - only if available
    if (policyDetailsInfo?.DepartureDate || policyDetailsInfo?.FromDateStr) {
      tableData[translate("Departure date", "تاريخ المغادرة")] =
        formatDateToEnglish(
          policyDetailsInfo?.DepartureDate || policyDetailsInfo?.FromDateStr,
        );
    }

    // Return Date - only if available
    if (policyDetailsInfo?.ReturnDate || policyDetailsInfo?.ToDateStr) {
      tableData[translate("Return date", "تاريخ العودة")] = formatDateToEnglish(
        policyDetailsInfo?.ReturnDate || policyDetailsInfo?.ToDateStr,
      );
    }

    // Plan - only if available
    if (policyDetailsInfo?.PlanName) {
      tableData[translate("Plan", "الخطة")] = policyDetailsInfo.PlanName;
    }

    // Add-ons - only winter sports and terrorism, only if available
    if (policyDetailsInfo?.Covers && policyDetailsInfo.Covers.length > 0) {
      const additionalCovers = policyDetailsInfo.Covers.filter(
        (cover) => cover.CoverCode === "CPER137" || cover.CoverCode === "CPER17",
      );

      if (additionalCovers.length > 0) {
        additionalCovers.forEach((cover, index) => {
          const coverKey =
            additionalCovers.length === 1
              ? translate("Add-on", "إضافة")
              : `${translate("Add-on", "إضافة")} ${index + 1}`;
          tableData[coverKey] =
            cover.CoverName ||
            cover.Name ||
            cover.Description ||
            cover.CoverCode;
        });
      }
    }

    return tableData;
  };

  /* Next */
  const continueClickHanlder = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={true}
      backClickHandler={handleNavigationToPolicyOverview}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHanlder}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHanlder}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-20 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important max767:!font-medium"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your policy details carefully before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام.",
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول",
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                policy_name="Travel Insurance"
                policy_label={"Travel Policy"}
                policy_number={policyDetailsInfo?.PolicyNumber || "N/A"}
                from_date={policyCardInfo.FromDateStr}
                to_date={policyCardInfo.ToDateStr}
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />

              {/* Motor-style table structure */}
              <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                <tbody>
                  {/* 1. Passenger 1 is always the policy holder (Party) — full info. */}
                  <tr>
                    <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                      {translate("Passenger", "مسافر")} 1
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {`${policyDetailsPartyInfo?.FirstName || ""} ${
                        policyDetailsPartyInfo?.LastName || ""
                      }`.trim() || "N/A"}
                    </td>
                  </tr>
                  {/* Family plans: additional members from the Passengers list.
                      Skip empty/placeholder entries (no name). */}
                  {(policyDetailsInfo?.Passengers || [])
                    .map((passenger) => passenger.FullName || passenger.FirstName)
                    .filter(Boolean)
                    .map((name, index) => (
                      <tr key={index}>
                        <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                          {translate("Passenger", "مسافر")} {index + 2}
                        </td>
                        <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                          {name}
                        </td>
                      </tr>
                    ))}

                  {/* 2. Departure Date */}
                  <tr>
                    <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                      {translate("Departure date", "تاريخ المغادرة")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {formatDateAsNumbers(
                        policyDetailsInfo?.DepartureDate ||
                          policyDetailsInfo?.FromDateStr,
                      )}
                    </td>
                  </tr>

                  {/* 3. Return Date */}
                  <tr>
                    <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                      {translate("Return date", "تاريخ العودة")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {formatDateAsNumbers(
                        policyDetailsInfo?.ReturnDate ||
                          policyDetailsInfo?.ToDateStr,
                      )}
                    </td>
                  </tr>

                  {/* 4. Plan (with proper name mapping) */}
                  <tr>
                    <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                      {translate("Plan", "الخطة")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {mapTravelPlanName(
                        policyDetailsInfo?.PlanName,
                        policyDetailsInfo?.PlanCode,
                      )}
                    </td>
                  </tr>

                  {/* 5. Add-ons - Show only CPER137 and CPER17 */}
                  {policyDetailsInfo?.Covers &&
                  policyDetailsInfo.Covers.filter(
                    (cover) =>
                      cover.CoverCode === "CPER137" ||
                      cover.CoverCode === "CPER17",
                  ).length > 0
                    ? policyDetailsInfo.Covers.filter(
                        (cover) =>
                          cover.CoverCode === "CPER137" ||
                          cover.CoverCode === "CPER17",
                      ).map((cover, index) => (
                        <tr key={index}>
                          <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r rtl:border-l rtl:border-r-0 text-[#5B6871] text-sm font-medium leading-6">
                            {translate("Add-on", "إضافة")} {index + 1}
                          </td>
                          <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                            {cover.CoverCode === "CPER17"
                              ? translate("Winter Activities", "الأنشطة الشتوية")
                              : cover.CoverCode === "CPER137"
                              ? translate("Summer Activities", "الأنشطة الصيفية")
                              : cover.CoverName || cover.Name || cover.Description || cover.CoverCode}
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default TravelCancellationReviewPage;
